import { useEffect, Component } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Lineage from "@/components/Lineage";
import Difference from "@/components/Difference";
import Roster from "@/components/Roster";
import RosterGallery from "@/components/RosterGallery";
import Services from "@/components/Services";
import Ecosystem from "@/components/Ecosystem";
import Team from "@/components/Team";
import InquiryForms from "@/components/InquiryForms";
import Footer from "@/components/Footer";
import CustomCursor from "@/components/CustomCursor";
import CookieConsent from "@/components/CookieConsent";
import ChatWidget from "@/components/ChatWidget";
import AdminPage from "@/pages/admin";
import AthleteProfilePage from "@/pages/athlete";
import LegalPage from "@/pages/legal";
import AuthCallback from "@/components/AuthCallback";
import Faq from "@/components/Faq";
import PlayerNews from "@/components/PlayerNews";
import { GuidesIndexPage, GuideArticlePage } from "@/pages/guides";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink p-8 text-center text-white">
          Something went wrong. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

function HomePage() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || href === "#") return;
      const el = document.querySelector(href);
      if (!el) return;
      e.preventDefault();
      history.pushState(null, "", href);
      window.dispatchEvent(new Event("hashchange"));
      lenis.scrollTo(el, { offset: -92 });
    };
    document.addEventListener("click", onClick);

    if (window.location.hash && window.location.hash !== "#") {
      let last = -1;
      let tries = 0;
      const scrollToHash = () => {
        const target = document.querySelector(window.location.hash);
        if (!target) return;
        const top = target.getBoundingClientRect().top + window.scrollY - 92;
        lenis.scrollTo(top, { force: true });
        if (Math.abs(top - last) > 4 && tries < 10) {
          tries += 1;
          last = top;
          setTimeout(scrollToHash, 300);
        }
      };
      setTimeout(scrollToHash, 450);
    }

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);

  if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <div className="relative min-h-screen bg-ink text-slate-50">
      <div className="noise-overlay" aria-hidden="true" />
      <CustomCursor />
      <CookieConsent />
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <RosterGallery />
        <Lineage />
        <Difference />
        <Roster />
        <Services />
        <Ecosystem />
        <Team />
        <InquiryForms />
        <Faq />
        <PlayerNews />
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/athlete/:id" element={<AthleteProfilePage />} />
          <Route path="/guides" element={<GuidesIndexPage />} />
          <Route path="/guides/:slug" element={<GuideArticlePage />} />
          <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
          <Route path="/terms" element={<LegalPage docKey="terms" />} />
          <Route path="/accessibility" element={<LegalPage docKey="accessibility" />} />
          <Route path="/social-responsibility" element={<LegalPage docKey="social" />} />
          <Route path="/human-rights" element={<LegalPage docKey="human-rights" />} />
          <Route path="/us-privacy-notice" element={<LegalPage docKey="us-notice" />} />
          <Route path="/client-privacy-notice" element={<LegalPage docKey="intl-notice" />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#161D29",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#F8FAFC",
            },
          }}
        />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
